// PURE deck arithmetic — pagination, pricing cascade, sentence truncation.
//
// Everything here is deterministic and import-free (no pg, no React, no src/server), so the
// bound resolver, the PPTX templates, the HTML preview and the tests all compute the SAME
// numbers from the same inputs. All px values are in the frozen 1280×720 slide space of the
// LCH01 v15 golden master.

// ────────────────────────────────────────────────────────────────────────────
// Table pagination (slide types 5 · arbeitsergebnisse_tabelle and 12 · anhang_register)
// ────────────────────────────────────────────────────────────────────────────

/** First table row starts at this y. */
export const TABLE_START_Y = 178;
/** A row must fit entirely above this y; otherwise it opens the next page. */
export const TABLE_MAX_Y = 640;
/** One wrapped line of a row block: 19.5px (two lines = 39px, per the brief). */
export const TABLE_LINE_HEIGHT = 19.5;
/** Vertical gap between rows. */
export const TABLE_ROW_GAP = 14;

export interface PaginateBlock {
  /** The block's text; '\n' forces a line break. */
  text: string;
  /** How many characters one line of this block holds at its column width/font. */
  charsPerLine: number;
  /** Optional clamp (e.g. requirement statements render at most 2 lines at w830). */
  maxLines?: number;
  /** Height of the block's first line (default: the table line height). */
  firstLineHeight?: number;
  /** Height of every further line (default: the table line height) — the name cell stacks a
   *  19.5px serif name over 15px subtitle lines, so its block is 34.5 / 49.5 like the master. */
  lineHeight?: number;
}

export interface PaginateRowInput<T> {
  /** Caller payload, returned untouched inside the placed rows. */
  data: T;
  /** The row's wrapping text blocks (name block, kurzform block, …); the tallest one sets the row height. */
  blocks: PaginateBlock[];
}

export interface PlacedRow<T> {
  data: T;
  /** Top of the row on its page. */
  y: number;
  height: number;
  /** Estimated line count per input block, same order. */
  blockLines: number[];
}

export interface PaginateOptions {
  startY?: number;
  maxY?: number;
  lineHeight?: number;
  rowGap?: number;
}

/**
 * Greedy word-wrap line estimate: words pack onto a line while they fit within
 * `charsPerLine`; a longer word occupies ceil(len / charsPerLine) lines on its own.
 * Explicit '\n' breaks are honoured. Empty text estimates 0 lines.
 */
export function estimateLines(text: string, charsPerLine: number, maxLines?: number): number {
  const t = text.trim();
  if (t === '' || charsPerLine <= 0) return 0;
  let lines = 0;
  for (const rawLine of t.split('\n')) {
    const line = rawLine.trim();
    if (line === '') continue;
    let used = 0;
    lines += 1;
    for (const word of line.split(/\s+/)) {
      if (word.length > charsPerLine) {
        // Overlong word: finish the current line, then let the word span its own lines.
        lines += Math.ceil(word.length / charsPerLine) - (used === 0 ? 1 : 0);
        used = word.length % charsPerLine || charsPerLine;
        continue;
      }
      const needed = used === 0 ? word.length : used + 1 + word.length;
      if (needed <= charsPerLine) {
        used = needed;
      } else {
        lines += 1;
        used = word.length;
      }
    }
  }
  return maxLines !== undefined ? Math.min(lines, maxLines) : lines;
}

/**
 * Paginate table rows into 1280×720 pages — the shared arithmetic of slide types 5 and 12.
 *
 * Row height = tallest block × 19.5px (one line 19.5, two lines 39, …), minimum one line.
 * Rows are placed from y=178, separated by a 14px gap; a row whose BOTTOM would cross y=640
 * starts the next page ("Seitenumbruch bei y>640"). The bottom check (not a start-y check)
 * is what reproduces the golden master's 7/9 split of the 16 LCH01 deliverables — a start-y
 * check would let an overflowing row bleed past the footline. A row taller than a whole page
 * still gets a page of its own rather than looping forever.
 */
export function paginateTabelle<T>(rows: PaginateRowInput<T>[], opts: PaginateOptions = {}): PlacedRow<T>[][] {
  const startY = opts.startY ?? TABLE_START_Y;
  const maxY = opts.maxY ?? TABLE_MAX_Y;
  const lineHeight = opts.lineHeight ?? TABLE_LINE_HEIGHT;
  const rowGap = opts.rowGap ?? TABLE_ROW_GAP;

  const pages: PlacedRow<T>[][] = [];
  let page: PlacedRow<T>[] = [];
  let y = startY;

  for (const row of rows) {
    const blockLines = row.blocks.map((b) => estimateLines(b.text, b.charsPerLine, b.maxLines));
    const blockHeights = row.blocks.map((b, i) => {
      const n = Math.max(1, blockLines[i]);
      const first = b.firstLineHeight ?? lineHeight;
      const rest = b.lineHeight ?? lineHeight;
      return first + (n - 1) * rest;
    });
    const height = Math.max(lineHeight, ...blockHeights);

    if (page.length > 0 && y + height > maxY) {
      pages.push(page);
      page = [];
      y = startY;
    }
    page.push({ data: row.data, y, height, blockLines });
    y += height + rowGap;
  }
  if (page.length > 0) pages.push(page);
  return pages;
}

// ────────────────────────────────────────────────────────────────────────────
// Pricing cascade (slide type 9 · preis)
// ────────────────────────────────────────────────────────────────────────────

export interface PricingDiscount {
  label: string;
  /** Percentage points, e.g. 25 for −25 %. */
  pct: number;
}

export interface PricingInput {
  months: number;
  /**
   * Billable days per month — the ALREADY-UTILISED planning figure (18, not 20 × 90 %).
   * The golden master prints "6 Monate × 18 Tage (90 % Auslastung) = 108 Tage": the 18
   * carries the utilisation; `utilizationPct` only annotates the line and never enters
   * the arithmetic. (Multiplying it in again would yield 97.2 days and break the
   * contract value below.)
   */
  daysPerMonth: number;
  /** Display annotation. Accepts a fraction (0.9) or percent (90); normalised to percent. */
  utilizationPct: number;
  dayRateChf: number;
  /** Applied sequentially, each to the already-discounted value. */
  discounts?: PricingDiscount[];
}

export interface PricingStep {
  label: string;
  pct: number;
  /** The deducted amount in whole CHF (printed "− CHF 37'260"). */
  amountChf: number;
  /** Running value after this step, whole CHF. */
  afterChf: number;
}

export interface PricingResult {
  /** months × daysPerMonth (see PricingInput.daysPerMonth). */
  fullDays: number;
  /** fullDays × dayRateChf, whole CHF ("Vollwert"). */
  fullValueChf: number;
  steps: PricingStep[];
  /** Final value after all discounts, whole CHF. */
  totalChf: number;
  /** Combined discount vs. the full value, whole percent ("− 40 % KOMBINIERT"). */
  combinedPct: number;
  /** totalChf / fullDays, whole CHF ("EFFEKTIV CHF 828 PRO TAG"). */
  effectiveDayRateChf: number;
  /** Normalised to percent (0.9 → 90) for the days line's annotation. */
  utilizationPct: number;
}

/**
 * The investment slide's calculation cascade: full days → full value → sequential
 * discounts → total, every CHF amount rounded to whole francs.
 *
 * Contract example (LCH01 v15, MUST hold — the golden test pins it):
 *   computePricing({ months: 6, daysPerMonth: 18, utilizationPct: 0.9, dayRateChf: 1380,
 *                    discounts: [{ label: 'NGO', pct: 25 }, { label: 'Laufzeit', pct: 20 }] })
 *   → fullDays 108 (6 × 18)
 *   → fullValueChf 149'040
 *   → −25 %: amount 37'260, after 111'780
 *   → −20 %: amount 22'356 (of 111'780 — sequential, not of the full value)
 *   → totalChf 89'424, combinedPct 40, effectiveDayRateChf 828
 */
export function computePricing(input: PricingInput): PricingResult {
  const utilizationPct = input.utilizationPct <= 1 ? input.utilizationPct * 100 : input.utilizationPct;
  const fullDays = input.months * input.daysPerMonth;
  const fullValueChf = Math.round(fullDays * input.dayRateChf);

  const steps: PricingStep[] = [];
  let running = fullValueChf;
  for (const discount of input.discounts ?? []) {
    const amountChf = Math.round(running * (discount.pct / 100));
    running -= amountChf;
    steps.push({ label: discount.label, pct: discount.pct, amountChf, afterChf: running });
  }

  const combinedPct = fullValueChf > 0 ? Math.round(((fullValueChf - running) / fullValueChf) * 100) : 0;
  const effectiveDayRateChf = fullDays > 0 ? Math.round(running / fullDays) : 0;
  return { fullDays, fullValueChf, steps, totalChf: running, combinedPct, effectiveDayRateChf, utilizationPct };
}

// ────────────────────────────────────────────────────────────────────────────
// Sentence-aware truncation
// ────────────────────────────────────────────────────────────────────────────

/**
 * Shorten `text` to at most `maxChars` characters without cutting mid-sentence:
 * the cut lands after the LAST full sentence that fits (terminator . ! ? …, an
 * immediately following closing quote/bracket included). When not even one sentence
 * fits, the fallback cuts at a word boundary and appends '…' (counted in the budget).
 * Text already within budget comes back trimmed and otherwise untouched.
 */
export function truncateAtSentence(text: string, maxChars: number): string {
  const t = text.trim();
  if (maxChars <= 0) return '';
  if (t.length <= maxChars) return t;

  const window = t.slice(0, maxChars);
  const terminator = /[.!?…]["'»›)”’]?/g;
  let cut = -1;
  for (let m = terminator.exec(window); m !== null; m = terminator.exec(window)) {
    cut = m.index + m[0].length;
  }
  if (cut > 0) return t.slice(0, cut).trim();

  const space = window.lastIndexOf(' ', maxChars - 1);
  const head = space > 0 ? window.slice(0, space) : window.slice(0, maxChars - 1);
  return `${head.trimEnd()}…`;
}

// ────────────────────────────────────────────────────────────────────────────
// Inline emphasis — **bold** runs in prose slots (ausgangslage, auftrag body2, zielbild cards)
// ────────────────────────────────────────────────────────────────────────────

export interface EmphasisRun { text: string; bold: boolean }

/** Split "a **b** c" into runs; unmatched asterisks stay literal. */
export function parseEmphasis(text: string): EmphasisRun[] {
  const runs: EmphasisRun[] = [];
  const re = /\*\*([^*]+)\*\*/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) runs.push({ text: text.slice(last, m.index), bold: false });
    runs.push({ text: m[1], bold: true });
    last = m.index + m[0].length;
  }
  if (last < text.length) runs.push({ text: text.slice(last), bold: false });
  return runs.length ? runs : [{ text, bold: false }];
}

/** The plain text of an emphasised string (for line estimates and budgets). */
export const stripEmphasis = (text: string): string => text.replace(/\*\*([^*]+)\*\*/g, '$1');

// ────────────────────────────────────────────────────────────────────────────
// Two-column block packing (slide type · anhang_arbeitsergebnisse, v15 pages 18–22)
// ────────────────────────────────────────────────────────────────────────────

/** First block top; blocks pack downwards, the column ends where the next would cross maxY. */
export const COLUMNS_START_Y = 150;
export const COLUMNS_MAX_Y = 640;
/** Vertical gap between two blocks in a column (v15: DEL-01 foot 291 → DEL-02 rule 313). */
export const COLUMNS_BLOCK_GAP = 22;

export interface ColumnBlockInput<T> {
  data: T;
  height: number;
}
export interface PlacedBlock<T> { data: T; y: number; height: number }

/**
 * Pack blocks into pages of two equal columns: left column first, then right, then a new
 * page. A block taller than a column still gets its own column rather than looping.
 */
export function paginateColumns<T>(
  blocks: ColumnBlockInput<T>[],
  opts: { startY?: number; maxY?: number; gap?: number } = {},
): [PlacedBlock<T>[], PlacedBlock<T>[]][] {
  const startY = opts.startY ?? COLUMNS_START_Y;
  const maxY = opts.maxY ?? COLUMNS_MAX_Y;
  const gap = opts.gap ?? COLUMNS_BLOCK_GAP;

  const pages: [PlacedBlock<T>[], PlacedBlock<T>[]][] = [];
  let cols: [PlacedBlock<T>[], PlacedBlock<T>[]] = [[], []];
  let col = 0;
  let y = startY;

  for (const block of blocks) {
    if (cols[col].length > 0 && y + block.height > maxY) {
      if (col === 0) { col = 1; } else { pages.push(cols); cols = [[], []]; col = 0; }
      y = startY;
    }
    cols[col].push({ data: block.data, y, height: block.height });
    y += block.height + gap;
  }
  if (cols[0].length > 0 || cols[1].length > 0) pages.push(cols);
  return pages;
}

/**
 * Height of one deliverable block in the annex-B column geometry: serif head (19.2) +
 * TERMIN line (from head top: +26, 11.2 high) + items from +46, each item 21px for a
 * one-line title plus 16px per extra wrapped line, and 3px foot (v15: 3 one-line items →
 * 139px; 4 items with one 2-line title → 160px).
 */
export function annexBlockHeight(itemLines: number[]): number {
  // v15: DEL-01 (3 × 1 line) = 139; DEL-27 (4 items, one 2-line title) = 160;
  // DEL-03 (4 items, one 2-line) = 160 → head 46 + Σ(21 + 16·extra) + 30 foot.
  const itemsH = itemLines.reduce((sum, n) => sum + 21 + Math.max(0, n - 1) * 16, 0);
  return 46 + itemsH + 30;
}

// ────────────────────────────────────────────────────────────────────────────
// Image framing — focal point + zoom over a cover-fit box (all image slots)
// ────────────────────────────────────────────────────────────────────────────

export interface ImageFrameBox {
  /** Scaled image size (px of the slide) that covers the box at the given zoom. */
  imgW: number;
  imgH: number;
  /** Offset of the visible box inside the scaled image (px, ≥ 0). */
  offX: number;
  offY: number;
}

/**
 * The cover crop with a focal point: scale the image to cover the box, multiply by zoom,
 * then slide the box over the image so that the focal point (fx/fy in %) sits at the same
 * relative position inside the box — exactly what CSS `object-fit: cover; object-position:
 * fx% fy%` does at zoom 100. The PPTX template crops the scaled image with these numbers,
 * the preview reproduces them in CSS, so both frame the same pixels.
 */
export function frameImage(
  naturalW: number, naturalH: number, boxW: number, boxH: number,
  fx = 50, fy = 50, zoom = 100,
): ImageFrameBox {
  if (naturalW <= 0 || naturalH <= 0 || boxW <= 0 || boxH <= 0) {
    return { imgW: boxW, imgH: boxH, offX: 0, offY: 0 };
  }
  const z = Math.min(3, Math.max(1, zoom / 100));
  const scale = Math.max(boxW / naturalW, boxH / naturalH) * z;
  const imgW = naturalW * scale;
  const imgH = naturalH * scale;
  const cx = Math.min(100, Math.max(0, fx)) / 100;
  const cy = Math.min(100, Math.max(0, fy)) / 100;
  const offX = Math.round((imgW - boxW) * cx * 100) / 100;
  const offY = Math.round((imgH - boxH) * cy * 100) / 100;
  return { imgW: Math.round(imgW * 100) / 100, imgH: Math.round(imgH * 100) / 100, offX, offY };
}
